import { buildAsturHeaders } from '@/endpoints/channel/asturHeaders';
import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import { utils } from '@/utils';
import type { ChannelApiResponse } from '@/types/endpoints';
import type { IntegrationAction, IntegrationActionOverride, IntegrationActionRunHistory } from '@/types/integration-actions';
import type { ReadRequestOptions, WriteRequestOptions } from '../requestOptions';

const validateIntegrationId = (integrationId: number) => {
  if (!integrationId || integrationId < 1) {
    throw new Error('Valid integration ID is required');
  }
};

const validateActionId = (actionId: number) => {
  if (!actionId || actionId < 1) {
    throw new Error('Valid action ID is required');
  }
};

const validateSubscriptionId = (subscriptionId: number) => {
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
};

const normalizeArrayResult = <T>(data: unknown): T[] => {
  return Array.isArray(data) ? data : [];
};

const extractApiErrorMessage = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  const directError = typeof record.error === 'string' ? record.error : null;
  const message = typeof record.message === 'string' ? record.message : null;
  const nestedError =
    record.errors && typeof record.errors === 'object' && typeof (record.errors as Record<string, unknown>).error === 'string'
      ? ((record.errors as Record<string, unknown>).error as string)
      : null;
  return directError ?? nestedError ?? message;
};

export type IntegrationActionRequestOptions = ReadRequestOptions;

export const getAvailableIntegrationActions = async (
  scope: RequestScope,
  integrationId: number,
  subscriptionId: number | string | null | undefined,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<IntegrationAction[]>> => {
  try {
    validateIntegrationId(integrationId);
    const res = await scopedChannelApi(scope).get(`astur/integration/action/available/${integrationId}`, {
      ...options,
      headers: buildAsturHeaders({ subscriptionId }),
    });
    const apiError = extractApiErrorMessage(res.data);
    if (apiError) {
      throw new Error(apiError);
    }
    return { data: normalizeArrayResult(res.data), status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

export const getIntegrationActionOverrides = async (
  scope: RequestScope,
  subscriptionId: number,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<IntegrationActionOverride[]>> => {
  try {
    validateSubscriptionId(subscriptionId);
    const res = await scopedChannelApi(scope).get(`astur/integration/action/override/${subscriptionId}`, {
      ...options,
      headers: buildAsturHeaders({ subscriptionId }),
    });
    const apiError = extractApiErrorMessage(res.data);
    if (apiError) {
      throw new Error(apiError);
    }
    return { data: normalizeArrayResult(res.data), status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

export const getIntegrationActionHistory = async (
  scope: RequestScope,
  args: {
    actionId: number;
    subscriptionId: number;
    limit?: number;
  },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<IntegrationActionRunHistory[]>> => {
  try {
    validateActionId(args.actionId);
    validateSubscriptionId(args.subscriptionId);
    const limit = args.limit ?? 25;
    const res = await scopedChannelApi(scope).get(`astur/integration/action/history/${args.actionId}/${args.subscriptionId}`, {
      ...options,
      headers: buildAsturHeaders({ subscriptionId: args.subscriptionId }),
      params: { limit },
    });
    const apiError = extractApiErrorMessage(res.data);
    if (apiError) {
      throw new Error(apiError);
    }
    return { data: normalizeArrayResult(res.data), status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

export const invokeIntegrationAction = async (
  scope: RequestScope,
  args: { actionId: number; subscriptionId: number },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<void>> => {
  try {
    validateActionId(args.actionId);
    validateSubscriptionId(args.subscriptionId);
    const res = await scopedChannelApi(scope).post(`astur/integration/action/invoke/${args.actionId}/${args.subscriptionId}`, null, {
      ...options,
      headers: buildAsturHeaders({ subscriptionId: args.subscriptionId }),
    });
    const apiError = extractApiErrorMessage(res.data);
    if (apiError) {
      throw new Error(apiError);
    }
    return { data: undefined, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
